/**
 * Data Health Dashboard Widget
 * Displays key metrics across all claims for data quality monitoring
 */
import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Brain, 
  AlertTriangle, 
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../utils/api';

interface DataHealthMetrics {
  categorization: {
    total_transactions: number;
    auto_categorized: number;
    auto_categorized_pct: number;
    manual_categorized: number;
    manual_categorized_pct: number;
    uncategorized: number;
    uncategorized_pct: number;
  };
  ai_confidence: {
    average_score: number;
    samples_count: number;
    grade: string;
  };
  response_time: {
    average_hours: number;
    average_days: number;
    samples_count: number;
    grade: string;
  };
  stale_clarifications: {
    total_pending: number;
    stale_count: number;
    stale_pct: number;
    claims_affected: number;
    threshold_days: number;
  };
  anomalies: {
    total_count: number;
    top_types: Array<{ type: string; count: number }>;
  };
  ai_workflow: {
    ai_parsed_requests: number;
    ai_approved_requests: number;
    approval_rate: number;
  };
  generated_at: string;
}

interface DataHealthWidgetProps {
  reportId?: string;
  compact?: boolean;
}

export default function DataHealthWidget({ reportId, compact = false }: DataHealthWidgetProps) {
  const [metrics, setMetrics] = useState<DataHealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      const data = await api.get<DataHealthMetrics>('/api/v1/analytics/data-health', { showErrorToast: false });
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getGradeStyle = (grade: string): React.CSSProperties => {
    switch (grade.toLowerCase()) {
      case 'excellent':
      case 'fast':
        return { color: 'var(--color-verified)', backgroundColor: 'transparent' };
      case 'good':
        return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
      case 'fair':
      case 'slow':
        return { color: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' };
      default:
        return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    }
  };

  if (loading && !metrics) {
    return (
      <div className="rounded-xl shadow-sm border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 rounded w-1/3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-24 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
            <div className="h-24 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl shadow-sm border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={fetchMetrics} className="ml-auto text-sm hover:underline" style={{ color: 'var(--color-needs-human-text)' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="rounded-xl shadow-sm border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }} data-testid="data-health-widget">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b cursor-pointer"
        style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-brand)' }}>
            <Activity className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Data Health Dashboard</h3>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Key metrics across all claims</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); fetchMetrics(); }}
            className={`p-2 rounded-lg transition ${refreshing ? 'animate-spin' : ''}`}
            title="Refresh metrics"
          >
            <RefreshCw className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
          {expanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          )}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Key Metrics - Clean vertical list for narrow containers */}
          <div className="space-y-3">
            {/* Auto-Categorization */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-verified)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Auto-Categorized</span>
              </div>
              <span className="text-sm font-semibold" style={{ color: 'var(--color-verified)' }}>
                {metrics.categorization.auto_categorized_pct}%
              </span>
            </div>

            {/* AI Confidence */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>AI Confidence</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs px-1.5 py-0.5 rounded" style={getGradeStyle(metrics.ai_confidence.grade)}>
                  {metrics.ai_confidence.grade}
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                  {metrics.ai_confidence.average_score}%
                </span>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Avg Response</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs px-1.5 py-0.5 rounded" style={getGradeStyle(metrics.response_time.grade)}>
                  {metrics.response_time.grade}
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--color-brand)' }}>
                  {metrics.response_time.average_days < 1
                    ? `${Math.round(metrics.response_time.average_hours)}h`
                    : `${metrics.response_time.average_days}d`
                  }
                </span>
              </div>
            </div>

            {/* Stale Items */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: metrics.stale_clarifications.stale_pct > 20 ? 'var(--color-brand)' : 'var(--color-text-dim)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Stale (7+ days)</span>
              </div>
              <span className="text-sm font-semibold" style={{ color: metrics.stale_clarifications.stale_pct > 20 ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)' }}>
                {metrics.stale_clarifications.stale_count}/{metrics.stale_clarifications.total_pending}
              </span>
            </div>
          </div>

          {/* Categorization Progress Bar */}
          <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border-faint)' }}>
            <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
              <span>Categorization</span>
              <span>{metrics.categorization.total_transactions.toLocaleString()} txns</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden flex" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <div
                className="transition-all"
                style={{ width: `${metrics.categorization.auto_categorized_pct}%`, backgroundColor: 'var(--color-verified)' }}
                title={`Auto: ${metrics.categorization.auto_categorized_pct}%`}
              />
              <div
                className="transition-all"
                style={{ width: `${metrics.categorization.manual_categorized_pct}%`, backgroundColor: 'var(--color-brand)' }}
                title={`Manual: ${metrics.categorization.manual_categorized_pct}%`}
              />
              <div
                className="transition-all"
                style={{ width: `${metrics.categorization.uncategorized_pct}%`, backgroundColor: 'var(--color-brand)' }}
                title={`Uncategorized: ${metrics.categorization.uncategorized_pct}%`}
              />
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[10px]" style={{ color: 'var(--color-text-dim)' }}>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-verified)' }} />Auto</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />Manual</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />Pending</span>
            </div>
          </div>

          {/* AI Workflow Stats - Compact */}
          <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border-faint)' }}>
            <div className="flex items-center justify-between text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
              <span className="flex items-center gap-1">
                <Brain className="w-3 h-3" />
                AI Workflow
              </span>
              <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{metrics.ai_workflow.approval_rate}% approved</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: 'var(--color-text-muted)' }}>{metrics.ai_workflow.ai_parsed_requests} parsed</span>
              <span style={{ color: 'var(--color-verified)' }}>{metrics.ai_workflow.ai_approved_requests} approved</span>
            </div>
          </div>

          {/* Anomalies - Only show if there are any */}
          {metrics.anomalies.total_count > 0 && (
            <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border-faint)' }}>
              <div className="flex items-center justify-between text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
                <span>Anomalies Detected</span>
                <span className="font-medium" style={{ color: 'var(--color-brand)' }}>{metrics.anomalies.total_count}</span>
              </div>
              {metrics.anomalies.top_types.slice(0, 2).map((anomaly, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs py-0.5">
                  <span className="truncate flex-1 pr-2" style={{ color: 'var(--color-text-muted)' }}>{anomaly.type}</span>
                  <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>{anomaly.count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Timestamp */}
          <p className="text-[10px] pt-1" style={{ color: 'var(--color-text-dim)' }}>
            Updated {new Date(metrics.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      )}
    </div>
  );
}
